import { AccountObjectFacade } from "../account-object/account-object.facade";
import { JobStatusSummary } from "./job-status-summary.model";
import { JobStatusSummaryApiFacade } from "./job-status-summary.api-facade";
import { JobStatusSummaryStoreFacade } from "./job-status-summary.store-facade";
import { combineLatest } from "rxjs/observable/combineLatest";
import { Observable } from "rxjs/Observable";
import { Account } from "../account/account.model";
import { Job } from "../job/job.model";
import { JobStatus } from "../job-status/job-status.model";
import { isDefined } from "ionic-angular/util/util";
import { of } from "rxjs/observable/of";
import { JobStatusSummaryItem } from "./job-status-summary-item.model";
import { IJobSummary } from "../../api/api-responses/job-summary.interface";
import { from } from "rxjs/observable/from";
import { zip } from "rxjs/observable/zip";
import { forkJoin } from "rxjs/observable/forkJoin";

interface IAccountJobs {
    account: Account;
    jobs: Array<Job>;
}

interface IAccountJobsJobStatuses {
    account: Account;
    jobs: Array<IJobSummary>;
    jobStatuses: Array<JobStatus>;
}

export class JobStatusSummaryFacade extends AccountObjectFacade<JobStatusSummary, JobStatusSummaryApiFacade, JobStatusSummaryStoreFacade> {

    syncAll(): Observable<any> {
        console.log("SYNC: %s", this.getEntity());
        return this.appFacade.objects.account.api.getAll()
            .concatMap(accounts => {
                const os = accounts.map(account => this.syncAccount(account));
                return zip(...os);
            })
            .catch(error => this.handleSyncError(error));
    }

    protected syncAccount(account: Account): Observable<any> {

        return this.appFacade.objects.user.store
            .getActive()
            .take(1)
            .switchMap(user => forkJoin(
                this.getJobsByAccountApi(account),
                this.appFacade.objects.jobStatus.store.getByAccountUser(account, user).take(1)
            ))
            .map(args => {
                const [jobs, jobStatuses] = args;
                return this.getSummary(account, jobs, jobStatuses);
            })
            .do(summary => {
                this.store.insert(summary);
            });
    }

    protected getJobsByAccountApi(account: Account): Observable<Array<Job>> {
        return this.appFacade.objects.job.api.getAll(account.id)
            .catch(response => {
                switch (response.status) {
                    case 404: {
                        return of([]);
                    }
                    default: {
                        Observable.throw(response);
                    }
                }
            });
    }

    protected getAccountJobsJobStatuses(accountJobs: IAccountJobs): Observable<IAccountJobsJobStatuses> {
        return this.appFacade.objects.user.store
            .getActive()
            .concatMap(user => this.appFacade.objects.jobStatus.store.getByAccountUser(accountJobs.account, user))
            .map(jobStatuses => {
                return {
                    account: accountJobs.account,
                    jobs: accountJobs.jobs,
                    jobStatuses
                } as IAccountJobsJobStatuses;
            });
    }

    protected getSummary(account: Account, jobs: Array<Job>, jobStatuses: Array<JobStatus>): JobStatusSummary {

        const jobsByJobStatus = {};

        jobStatuses.forEach(status => {
            jobsByJobStatus[status.id] = 0;
        });

        jobs.forEach(job => {

            if (!isDefined(jobsByJobStatus[job.jobStatus])) {
                jobsByJobStatus[job.jobStatus] = 0;
            }

            jobsByJobStatus[job.jobStatus]++;
        });

        const items = jobStatuses
            .map(jobStatus => {
                return new JobStatusSummaryItem({
                    jobStatus: jobStatus.id,
                    title: jobStatus.title,
                    count: jobsByJobStatus[jobStatus.id],
                    sequence: jobStatus.sequence
                });
            })
            .sort((a, b) => a.sequence - b.sequence);

        return new JobStatusSummary({
            id: account.id,
            account: account.id,
            items: items || []
        });
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/job-status-summary-item/job-status-summary.facade.ts